import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input, notification } from "antd";
import { PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { useCallback, useRef, useState } from "react";
import { getRootStore } from "@/stores/RootStore";
import { canWriteChat } from "@/utils/permissions";
import styles from "@/styles/MessageComposer.module.less";
const { TextArea } = Input;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/gif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
const MessageComposer = observer(({ channelId, rootId, disabled }) => {
    const { chatStore, authStore } = getRootStore();
    const [text, setText] = useState("");
    const [pendingFile, setPendingFile] = useState(null);
    const fileInputRef = useRef(null);
    const canWrite = canWriteChat(authStore.userRole);
    const isDisabled = disabled || !canWrite;
    const isSending = chatStore.isSending;
    const canSend = (text.trim().length > 0 || pendingFile !== null) && !isSending;
    const handleSend = useCallback(async () => {
        if (!canSend)
            return;
        let fileIds;
        if (pendingFile) {
            fileIds = await chatStore.uploadFile(pendingFile);
            if (fileIds.length === 0 && !text.trim()) {
                // Upload failed and no text to send
                return;
            }
        }
        const messageText = text.trim();
        if (messageText || (fileIds && fileIds.length > 0)) {
            await chatStore.sendMessage(messageText, fileIds);
            setText("");
            setPendingFile(null);
        }
    }, [canSend, text, pendingFile, chatStore]);
    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);
    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file)
            return;
        if (file.size > MAX_FILE_SIZE) {
            notification.warning({
                message: "Файл слишком большой",
                description: "Максимальный размер файла — 10 МБ",
            });
            return;
        }
        if (!ALLOWED_TYPES.has(file.type)) {
            notification.warning({
                message: "Неподдерживаемый формат",
                description: "Допустимые форматы: JPG, PNG, GIF, PDF, DOC, DOCX",
            });
            return;
        }
        setPendingFile(file);
        // Reset input so same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);
    const handleAttachClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);
    return (_jsxs("div", { children: [pendingFile && (_jsxs("div", { className: styles.filePreview, children: [_jsx(PaperClipOutlined, {}), _jsx("span", { className: styles.fileName, children: pendingFile.name }), _jsx(Button, { type: "link", size: "small", onClick: () => setPendingFile(null), children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] })), _jsxs("div", { className: styles.container, children: [_jsx(TextArea, { className: styles.textArea, placeholder: "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435...", autoSize: { minRows: 1, maxRows: 4 }, value: text, onChange: (e) => setText(e.target.value), onKeyDown: handleKeyDown, disabled: isDisabled }), _jsxs("div", { className: styles.actions, children: [_jsx("input", { ref: fileInputRef, type: "file", className: styles.hiddenInput, onChange: handleFileChange, accept: ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx" }), _jsx(Button, { type: "text", icon: _jsx(PaperClipOutlined, {}), className: styles.attachButton, onClick: handleAttachClick, disabled: isDisabled }), _jsx(Button, { type: "primary", icon: _jsx(SendOutlined, {}), className: styles.sendButton, onClick: handleSend, disabled: !canSend || isDisabled, loading: isSending })] })] })] }));
});
MessageComposer.displayName = "MessageComposer";
export default MessageComposer;
